import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LayoutDashboard, Users, Grid, Settings, LogOut, Plus, ShieldCheck, 
  Activity, Trash2, Globe, Eye, FileText, X, MonitorPlay, FolderKanban, 
  Briefcase, ArrowRight, Image as ImageIcon, CheckCircle, AlertTriangle, Key, Edit2, Lock, Mail, Copy
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext'; 
import LanguageToggle from '../components/LanguageToggle'; 
import TamkeenLogo from '../components/TamkeenLogo';

// 1. استيراد المكتبات الصحيحة للهندسة النظيفة
import { db, firebaseConfig } from '../firebase';
import { collection, onSnapshot, doc, setDoc, deleteDoc, updateDoc } from 'firebase/firestore';
import { initializeApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword, signOut, sendPasswordResetEmail } from 'firebase/auth';

// 2. إنشاء اتصال ثانوي لتكوين الحسابات دون طرد الإدارة المركزية من جلستها
const secondaryApp = initializeApp(firebaseConfig, "SecondaryApp");
const secondaryAuth = getAuth(secondaryApp);

const AdminDashboard = () => {
  const { logout } = useAuth();
  const { t, language } = useLanguage(); 
  
  const [activeTab, setActiveTab] = useState('overview'); 
  
  const [educators, setEducators] = useState([]);
  const [units, setUnits] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [sharedActivities, setSharedActivities] = useState([]);

  const [newEduName, setNewEduName] = useState('');
  const [newEduWorkshop, setNewEduWorkshop] = useState(''); 

  const [selectedUnit, setSelectedUnit] = useState(null); 
  const [unitInternalTab, setUnitInternalTab] = useState('workshops'); 

  const [isAddUnitModalOpen, setIsAddUnitModalOpen] = useState(false);
  const [newUnitData, setNewUnitData] = useState({ name: '', manager: '', image: '' });

  const [isAddWorkshopModalOpen, setIsAddWorkshopModalOpen] = useState(false);
  const [newWorkshopData, setNewWorkshopData] = useState({ name: '', description: '' });

  const [isEditAccountModalOpen, setIsEditAccountModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [previewActivity, setPreviewActivity] = useState(null); 

  const [isWorkshopSettingsOpen, setIsWorkshopSettingsOpen] = useState(false);
  const [editingWorkshop, setEditingWorkshop] = useState(null);
  const [editWsName, setEditWsName] = useState('');
  const [editWsDesc, setEditWsDesc] = useState('');
  const [editWsEducators, setEditWsEducators] = useState([]);

  // حالة العرض الآمن لبيانات الحساب الجديد
  const [newAccountData, setNewAccountData] = useState(null);

  useEffect(() => {
    const unsubUnits = onSnapshot(collection(db, 'units'), (snapshot) => {
      setUnits(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    const unsubWorkshops = onSnapshot(collection(db, 'workshops'), (snapshot) => {
      setWorkshops(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    const unsubEducators = onSnapshot(collection(db, 'educators'), (snapshot) => {
      setEducators(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    const unsubLibrary = onSnapshot(collection(db, 'library'), (snapshot) => {
      setSharedActivities(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    return () => {
      unsubUnits();
      unsubWorkshops();
      unsubEducators();
      unsubLibrary();
    };
  }, []);

  useEffect(() => {
    if (workshops.length > 0 && !newEduWorkshop) {
      setNewEduWorkshop(workshops[0].name);
    } else if (workshops.length === 0) {
      setNewEduWorkshop('');
    }
  }, [workshops, newEduWorkshop]);

  const generateEmail = (name) => {
    const englishName = name.split(' ')[0].toLowerCase().replace(/[^a-z]/g, 'edu');
    return `${englishName}${Math.floor(Math.random() * 1000)}@tamkeen.ma`;
  };

  const generateStrongPassword = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789!@#';
    let pass = '';
    for(let i = 0; i < 8; i++) pass += chars[Math.floor(Math.random() * chars.length)];
    return pass;
  };

  // 3. بناء الحسابات معمارياً (Auth + Firestore) وإظهار نافذة النسخ
  const handleAddEducator = async (e) => {
    e.preventDefault();
    if (workshops.length === 0) {
      alert(t('admin.alerts.createWorkshopFirst'));
      return;
    }
    if (!newEduName.trim() || !newEduWorkshop) { alert(t('admin.alerts.enterNameAndWorkshop')); return; }
    
    const email = generateEmail(newEduName);
    const password = generateStrongPassword();
    
    try {
      // أ- إنشاء الحساب المشفّر في Firebase Auth عبر الاتصال الثانوي
      const userCredential = await createUserWithEmailAndPassword(secondaryAuth, email, password);
      const uid = userCredential.user.uid;

      // ب- تسجيل البيانات الوظيفية في قاعدة البيانات مع منع تخزين كلمة المرور أمنياً
      const newEducator = { 
        name: newEduName, 
        email: email, 
        workshop: newEduWorkshop, 
        status: 'نشط' 
      };
      
      await setDoc(doc(db, 'educators', uid), newEducator);
      await signOut(secondaryAuth); // تنظيف الاتصال الثانوي
      
      // ج- تفعيل نافذة العرض المؤقتة
      setNewAccountData({ email, password, name: newEduName });
      setNewEduName('');
    } catch (error) {
      console.error("Auth Error:", error);
      alert("حدث خطأ في بناء الحساب المشفّر.");
    }
  };

  // 4. تعديل الحسابات صار يقتصر على الصلاحيات، وإذا نسي كلمة المرور نرسل له رابط تغيير آمن
  const handleSaveAccountEdit = async (e) => {
    e.preventDefault();
    await updateDoc(doc(db, 'educators', editingAccount.id), {
      name: editingAccount.name,
      workshop: editingAccount.workshop
    });
    setIsEditAccountModalOpen(false);
    setEditingAccount(null);
  };

  const handleResetPassword = async (email) => {
    try {
      const auth = getAuth();
      await sendPasswordResetEmail(auth, email);
      alert("تم إرسال رابط تعيين كلمة المرور إلى البريد الإلكتروني الخاص بالإطار.");
    } catch (error) {
      alert("تعذر إرسال رابط التعيين.");
    }
  };

  const handleDeleteEducator = async (id) => {
    if(window.confirm(t('admin.alerts.deleteAccountConfirm'))) {
      await deleteDoc(doc(db, 'educators', id));
    }
  };

  const handleAddUnit = async (e) => {
    e.preventDefault();
    if (!newUnitData.name || !newUnitData.manager) return;
    const newId = `unit_${Date.now()}`;
    await setDoc(doc(db, 'units', newId), {
      name: newUnitData.name, 
      manager: newUnitData.manager, 
      image: newUnitData.image || 'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=600&q=80'
    });
    setNewUnitData({ name: '', manager: '', image: '' });
    setIsAddUnitModalOpen(false);
  };

  const handleDeleteUnit = async (id, e) => {
    e.stopPropagation(); 
    if(window.confirm(t('admin.alerts.deleteUnitConfirm'))) {
      await deleteDoc(doc(db, 'units', id));
      const unitWorkshops = workshops.filter(w => w.unitId === id);
      for (let w of unitWorkshops) {
         await deleteDoc(doc(db, 'workshops', w.id));
      }
    }
  };

  const handleAddWorkshop = async (e) => {
    e.preventDefault();
    if (!newWorkshopData.name || !newWorkshopData.description) return;
    const newId = `ws_${Date.now()}`;
    await setDoc(doc(db, 'workshops', newId), {
      unitId: selectedUnit.id, 
      name: newWorkshopData.name, 
      description: newWorkshopData.description
    });
    setNewWorkshopData({ name: '', description: '' });
    setIsAddWorkshopModalOpen(false);
  };

  const handleDeleteWorkshop = async (id) => {
    if(window.confirm(t('admin.alerts.deleteWorkshopConfirm'))) {
      await deleteDoc(doc(db, 'workshops', id));
    }
  };

  const openWorkshopSettings = (ws) => {
    setEditingWorkshop(ws);
    setEditWsName(ws.name);
    setEditWsDesc(ws.description);
    const assignedEducators = educators.filter(e => e.workshop === ws.name);
    setEditWsEducators(assignedEducators.map(e => ({ id: e.id, name: e.name })));
    setIsWorkshopSettingsOpen(true);
  };

  const saveWorkshopSettings = async (e) => {
    e.preventDefault();
    const oldName = editingWorkshop.name;

    await updateDoc(doc(db, 'workshops', editingWorkshop.id), {
      name: editWsName,
      description: editWsDesc
    });

    for (let editedEdu of editWsEducators) {
      if (editWsName !== oldName) {
         await updateDoc(doc(db, 'educators', editedEdu.id), { workshop: editWsName });
      }
    }

    setIsWorkshopSettingsOpen(false);
    setEditingWorkshop(null);
    alert(t('admin.alerts.wsSettingsSaved'));
  };

  const handleDeleteActivity = async (id) => {
    if(window.confirm(t('admin.alerts.deleteActivityConfirm'))) {
      await deleteDoc(doc(db, 'library', id));
      setPreviewActivity(null);
    }
  };

  const renderOverview = () => (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10 relative z-10">
        <StatCard title={t('admin.stats.totalEducators')} value={educators.length.toString()} trend={t('admin.stats.activeAccounts')} />
        <StatCard title={t('admin.stats.registeredWorkshops')} value={workshops.length.toString()} trend={`${t('admin.stats.distributedOn')} ${units.length} ${t('admin.stats.unitsWord')}`} />
        <StatCard title={t('admin.stats.libraryActivities')} value={sharedActivities.length.toString()} trend={t('admin.stats.readyToUse')} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 relative z-10">
        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }} className="lg:col-span-1 bg-white/60 backdrop-blur-xl border border-white/80 p-6 rounded-3xl shadow-sm h-fit">
          <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2"><Plus className="w-5 h-5 text-indigo-600" /> {t('admin.addEdu.title')}</h3>
          
          {workshops.length === 0 ? (
            <div className="bg-amber-50 border border-amber-200 text-amber-700 p-4 rounded-xl text-sm font-bold flex gap-2">
              <AlertTriangle className="w-5 h-5 shrink-0"/>
              {t('admin.addEdu.warningNoWorkshop')}
            </div>
          ) : (
            <form onSubmit={handleAddEducator} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">{t('admin.addEdu.fullName')}</label>
                <input type="text" value={newEduName} onChange={(e) => setNewEduName(e.target.value)} className="w-full p-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:outline-none transition-all" placeholder={t('admin.addEdu.namePlaceholder')} />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">{t('admin.addEdu.workshopLabel')}</label>
                <select value={newEduWorkshop} onChange={(e) => setNewEduWorkshop(e.target.value)} className="w-full p-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:outline-none transition-all">
                  {workshops.map(ws => {
                    const parentUnit = units.find(u => u.id === ws.unitId);
                    return (
                      <option key={ws.id} value={ws.name}>{ws.name} ({parentUnit ? parentUnit.name : t('admin.addEdu.unspecified')})</option>
                    );
                  })}
                </select>
              </div>
              <button type="submit" className="w-full py-3.5 mt-2 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-colors shadow-lg shadow-slate-900/20 flex justify-center items-center gap-2"><Plus className="w-4 h-4"/> {t('admin.addEdu.btnSubmit')}</button>
            </form>
          )}
        </motion.div>

        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.3 }} className="lg:col-span-2 bg-white/60 backdrop-blur-xl border border-white/80 p-6 rounded-3xl shadow-sm overflow-hidden">
          <div className="flex justify-between items-center mb-6">
             <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2"><Users className="w-5 h-5 text-emerald-500" /> {t('admin.eduSummary.title')}</h3>
             <span className="text-xs font-bold bg-slate-100 text-slate-500 px-3 py-1 rounded-full">{t('admin.eduSummary.editHint')}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-start">
              <thead>
                <tr className="border-b border-slate-200 text-slate-400 text-sm">
                  <th className="pb-3 font-medium px-2">{t('admin.eduSummary.colName')}</th>
                  <th className="pb-3 font-medium px-2">{t('admin.eduSummary.colWorkshop')}</th>
                  <th className="pb-3 font-medium px-2">{t('admin.eduSummary.colStatus')}</th>
                </tr>
              </thead>
              <tbody>
                {educators.length > 0 ? educators.slice(0, 5).map((edu) => (
                  <tr key={edu.id} className="border-b border-slate-100 hover:bg-slate-50/80 transition-colors">
                    <td className="py-4 px-2 font-bold text-slate-800">{edu.name}</td>
                    <td className="py-4 px-2"><span className="px-3 py-1 bg-indigo-50 text-indigo-600 rounded-lg text-xs font-bold border border-indigo-100 inline-block">{edu.workshop}</span></td>
                    <td className="py-4 px-2"><span className="text-xs font-bold text-emerald-500 flex items-center gap-1"><CheckCircle className="w-3 h-3"/> {edu.status}</span></td>
                  </tr>
                )) : (
                  <tr><td colSpan="3" className="py-8 text-center text-slate-400 font-bold">{t('admin.eduSummary.empty')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </>
  );

  const renderAccountManagement = () => (
    <div className="space-y-6 relative z-10">
      <div className="flex justify-between items-center bg-white/60 backdrop-blur-xl p-6 rounded-3xl border border-white/80 shadow-sm mb-8">
        <div>
          <h2 className="text-xl font-extrabold text-slate-800 flex items-center gap-2 mb-1"><Key className="w-6 h-6 text-amber-500" /> {t('admin.accountMgmt.title')}</h2>
          <p className="text-sm text-slate-500">إدارة حسابات الأطر (لأسباب أمنية، كلمات المرور مشفرة ولا تظهر هنا)</p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden">
         <table className="w-full text-start">
            <thead>
              <tr className="border-b border-slate-200 text-slate-500 text-sm bg-slate-50">
                <th className="p-5 font-bold">{t('admin.accountMgmt.colEdu')}</th>
                <th className="p-5 font-bold">{t('admin.accountMgmt.colLogin')}</th>
                <th className="p-5 font-bold text-center">{t('admin.accountMgmt.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {educators.length > 0 ? educators.map((edu) => (
                <tr key={edu.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                  <td className="p-5">
                    <p className="font-bold text-slate-800 text-base">{edu.name}</p>
                    <p className="text-xs font-bold text-slate-500 mt-1 bg-slate-100 px-2 py-1 rounded w-fit">{edu.workshop}</p>
                  </td>
                  <td className="p-5">
                    <div className="flex flex-col gap-2">
                       <span className="text-sm font-bold text-slate-700 bg-slate-100 px-3 py-1.5 rounded-lg border border-slate-200 w-fit">{edu.email}</span>
                       <button onClick={() => handleResetPassword(edu.email)} className="text-xs font-bold text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg border border-blue-200 w-fit flex items-center gap-2 transition-colors">
                         <Mail className="w-3 h-3" /> إرسال رابط إعادة ضبط كلمة المرور
                       </button>
                    </div>
                  </td>
                  <td className="p-5">
                     <div className="flex justify-center items-center gap-2">
                        <button onClick={() => { setEditingAccount(edu); setIsEditAccountModalOpen(true); }} className="p-2.5 bg-slate-50 text-slate-600 hover:bg-slate-800 hover:text-white rounded-xl transition-colors border border-slate-200 hover:border-transparent" title={t('admin.accountMgmt.editBtn')}>
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDeleteEducator(edu.id)} className="p-2.5 bg-red-50 text-red-600 hover:bg-red-500 hover:text-white rounded-xl transition-colors border border-red-100 hover:border-transparent" title={t('admin.accountMgmt.deleteBtn')}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                     </div>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan="3" className="py-12 text-center text-slate-400 font-bold">{t('admin.accountMgmt.empty')}</td></tr>
              )}
            </tbody>
         </table>
      </div>
    </div>
  );

  const renderUnitsAndWorkshops = () => {
    if (!selectedUnit) {
      return (
        <div className="space-y-6 relative z-10">
          <div className="flex justify-between items-center bg-white/60 backdrop-blur-xl p-6 rounded-3xl border border-white/80 shadow-sm mb-8">
            <div>
              <h2 className="text-xl font-extrabold text-slate-800 flex items-center gap-2 mb-1"><FolderKanban className="w-6 h-6 text-emerald-600" /> {t('admin.unitsWorkshops.title')}</h2>
              <p className="text-sm text-slate-500">{t('admin.unitsWorkshops.subtitle')}</p>
            </div>
            <button onClick={() => setIsAddUnitModalOpen(true)} className="bg-emerald-600 hover:bg-emerald-500 text-white px-5 py-2.5 rounded-xl font-bold flex items-center gap-2 shadow-lg shadow-emerald-600/20 transition-all">
              <Plus className="w-4 h-4" /> {t('admin.unitsWorkshops.addUnitBtn')}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {units.length > 0 ? units.map(unit => {
              const unitWorkshops = workshops.filter(w => w.unitId === unit.id);
              return (
                <motion.div key={unit.id} whileHover={{ y: -5 }} onClick={() => setSelectedUnit(unit)} className="bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden flex flex-col group cursor-pointer">
                  <div className="h-48 w-full relative overflow-hidden bg-slate-100">
                    <img src={unit.image} alt={unit.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-transparent"></div>
                    <div className="absolute bottom-4 end-4 start-4">
                      <h3 className="text-xl font-extrabold text-white mb-1 drop-shadow-md">{unit.name}</h3>
                      <p className="text-xs font-bold text-emerald-300 flex items-center gap-1"><ShieldCheck className="w-4 h-4"/> {t('admin.unitsWorkshops.unitManager')} {unit.manager}</p>
                    </div>
                  </div>
                  <div className="p-5 flex justify-between items-center bg-white">
                    <div className="flex items-center gap-2 text-slate-600 font-bold text-sm">
                      <Briefcase className="w-5 h-5 text-slate-400" /> {unitWorkshops.length} {t('admin.unitsWorkshops.affiliatedWorkshops')}
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={(e) => handleDeleteUnit(unit.id, e)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"><Trash2 className="w-4 h-4" /></button>
                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 group-hover:bg-emerald-100 group-hover:text-emerald-600 transition-colors">
                         <ArrowRight className={`w-4 h-4 ${language === 'fr' ? 'rotate-180' : ''}`} />
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            }) : (
              <div className="col-span-full py-20 text-center border-2 border-dashed border-slate-300 rounded-3xl text-slate-500">
                 <FolderKanban className="w-16 h-16 mx-auto mb-4 opacity-30" />
                 <p className="text-lg font-bold">{t('admin.unitsWorkshops.emptyUnits')}</p>
                 <p className="text-sm mt-2">{t('admin.unitsWorkshops.emptyUnitsHint')}</p>
              </div>
            )}
          </div>
        </div>
      );
    }

    const unitWorkshops = workshops.filter(w => w.unitId === selectedUnit.id);
    const unitWorkshopNames = unitWorkshops.map(w => w.name);
    const unitEducators = educators.filter(edu => unitWorkshopNames.includes(edu.workshop));

    return (
      <div className="space-y-6 relative z-10">
        <div className="bg-white/80 backdrop-blur-xl p-6 rounded-3xl border border-white shadow-sm flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            <button onClick={() => setSelectedUnit(null)} className="w-10 h-10 flex items-center justify-center bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl transition-colors">
               <ArrowRight className={`w-5 h-5 ${language === 'fr' ? 'rotate-180' : ''}`} />
            </button>
            <div>
              <h2 className="text-xl font-extrabold text-slate-800">{selectedUnit.name}</h2>
              <p className="text-sm text-slate-500 font-bold">{t('admin.unitsWorkshops.unitSpace')} {selectedUnit.manager}</p>
            </div>
          </div>
          <div className="flex bg-slate-100 p-1 rounded-xl">
            <button onClick={() => setUnitInternalTab('workshops')} className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${unitInternalTab === 'workshops' ? 'bg-white text-emerald-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>{t('admin.unitsWorkshops.tabWorkshops')}</button>
            <button onClick={() => setUnitInternalTab('educators')} className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${unitInternalTab === 'educators' ? 'bg-white text-emerald-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>{t('admin.unitsWorkshops.tabEducators')}</button>
          </div>
        </div>

        {unitInternalTab === 'workshops' ? (
          <div>
             <div className="flex justify-end mb-6">
                <button onClick={() => setIsAddWorkshopModalOpen(true)} className="bg-slate-900 hover:bg-slate-800 text-white px-5 py-2.5 rounded-xl font-bold flex items-center gap-2 shadow-lg shadow-slate-900/20 transition-all"><Plus className="w-4 h-4" /> {t('admin.unitsWorkshops.addWorkshopBtn')}</button>
             </div>
             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {unitWorkshops.map(ws => (
                  <div key={ws.id} className="bg-white border border-slate-200 p-6 rounded-3xl shadow-sm hover:border-emerald-200 transition-colors">
                    <div className="flex justify-between items-start mb-4">
                      <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center text-emerald-600"><Briefcase className="w-6 h-6" /></div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => openWorkshopSettings(ws)} className="p-2 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors" title={t('admin.unitsWorkshops.workshopSettingsBtn')}><Settings className="w-4 h-4" /></button>
                        <button onClick={() => handleDeleteWorkshop(ws.id)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title={t('admin.unitsWorkshops.deleteWorkshopBtn')}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-slate-800 mb-2">{ws.name}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed mb-4">{ws.description}</p>
                    <div className="pt-4 border-t border-slate-100 flex items-center gap-2 text-xs font-bold text-slate-400">
                      <Users className="w-4 h-4"/> {educators.filter(e => e.workshop === ws.name).length} {t('admin.unitsWorkshops.assignedEducators')}
                    </div>
                  </div>
                ))}
                {unitWorkshops.length === 0 && (
                  <div className="col-span-full py-12 text-center border-2 border-dashed border-slate-300 rounded-3xl text-slate-500">
                    <Briefcase className="w-12 h-12 mx-auto mb-3 opacity-20" />
                    <p className="font-bold">{t('admin.unitsWorkshops.emptyWorkshops')}</p>
                  </div>
                )}
             </div>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden">
             <div className="p-6 border-b border-slate-100 bg-slate-50">
               <h3 className="font-bold text-slate-700 flex items-center gap-2"><Users className="w-5 h-5 text-emerald-500"/> {t('admin.unitsWorkshops.autoAssignedTitle')}</h3>
             </div>
             <table className="w-full text-start">
                <thead>
                  <tr className="border-b border-slate-200 text-slate-400 text-sm bg-white">
                    <th className="p-4 font-medium">{t('admin.unitsWorkshops.colName')}</th>
                    <th className="p-4 font-medium">{t('admin.unitsWorkshops.colEmail')}</th>
                    <th className="p-4 font-medium">{t('admin.unitsWorkshops.colWorkshop')}</th>
                    <th className="p-4 font-medium">{t('admin.unitsWorkshops.colStatus')}</th>
                  </tr>
                </thead>
                <tbody>
                  {unitEducators.map((edu) => (
                    <tr key={edu.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                      <td className="p-4 font-bold text-slate-800">{edu.name}</td>
                      <td className="p-4 text-slate-500 text-sm">{edu.email}</td>
                      <td className="p-4"><span className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-bold border border-emerald-100 inline-block">{edu.workshop}</span></td>
                      <td className="p-4"><span className="text-xs font-bold text-emerald-500 flex items-center gap-1"><CheckCircle className="w-3 h-3"/> {edu.status}</span></td>
                    </tr>
                  ))}
                  {unitEducators.length === 0 && (
                     <tr><td colSpan="4" className="p-8 text-center text-slate-500 font-bold">{t('admin.unitsWorkshops.emptyEducators')}</td></tr>
                  )}
                </tbody>
              </table>
          </div>
        )}
      </div>
    );
  };

  const renderLibraryMonitor = () => (
    <div className="space-y-6 relative z-10">
      <div className="bg-white/60 backdrop-blur-xl border border-white/80 p-6 rounded-3xl shadow-sm flex items-center justify-between mb-8">
         <div>
           <h2 className="text-xl font-extrabold text-slate-800 flex items-center gap-2 mb-1"><Globe className="w-6 h-6 text-blue-600" /> {t('admin.libraryMonitor.title')}</h2>
           <p className="text-sm text-slate-500">{t('admin.libraryMonitor.subtitle')}</p>
         </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {sharedActivities.length > 0 ? sharedActivities.map(activity => (
          <motion.div key={activity.id} whileHover={{ y: -5 }} className="bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden flex flex-col group">
            <div className="h-40 w-full relative overflow-hidden bg-slate-100">
               <img src={activity.coverImage} alt={activity.title} className="w-full h-full object-cover" />
            </div>
            <div className="p-5 flex-1 flex flex-col">
              <h3 className="text-lg font-bold text-slate-800 mb-2 line-clamp-2">{activity.title}</h3>
              <p className="text-xs text-slate-500 mb-4 font-bold flex items-center gap-1"><Users className="w-3.5 h-3.5"/> {t('admin.libraryMonitor.designBy')} {activity.author}</p>
              <div className="mt-auto pt-4 border-t border-slate-100 flex gap-2">
                <button onClick={() => setPreviewActivity(activity)} className="flex-1 bg-slate-100 text-slate-700 hover:bg-slate-200 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2"><Eye className="w-4 h-4" /> {t('admin.libraryMonitor.previewBtn')}</button>
                <button onClick={() => handleDeleteActivity(activity.id)} className="p-2.5 bg-red-50 text-red-600 hover:bg-red-500 hover:text-white rounded-xl"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </motion.div>
        )) : (
          <div className="col-span-full py-12 text-center text-slate-500 font-bold">{t('admin.libraryMonitor.empty')}</div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex font-sans relative overflow-hidden">
      <LanguageToggle />

      <motion.aside initial={{ x: 100, opacity: 0 }} animate={{ x: 0, opacity: 1 }} className="w-72 bg-white border-e border-slate-200 flex flex-col justify-between hidden md:flex z-20 shadow-xl shadow-slate-200/50 relative shrink-0 pt-16">
        <div>
          <div className="p-8 border-b border-slate-100 flex flex-col items-center">
            <TamkeenLogo className="w-16 h-16 mb-4" />
            <h2 className="text-xl font-bold text-slate-800">{t('admin.sidebar.title')}</h2>
            <p className="text-xs text-emerald-500 font-semibold mt-1 flex items-center gap-1"><ShieldCheck className="w-3 h-3"/> {t('admin.sidebar.subtitle')}</p>
          </div>
          
          <nav className="p-4 space-y-2">
            <SidebarItem icon={<LayoutDashboard />} label={t('admin.sidebar.tabOverview')} active={activeTab === 'overview'} onClick={() => setActiveTab('overview')} />
            <SidebarItem icon={<Key />} label={t('admin.sidebar.tabAccounts')} active={activeTab === 'accounts'} onClick={() => setActiveTab('accounts')} />
            <SidebarItem icon={<FolderKanban />} label={t('admin.sidebar.tabUnits')} active={activeTab === 'units'} onClick={() => setActiveTab('units')} />
            <SidebarItem icon={<Globe />} label={t('admin.sidebar.tabLibrary')} active={activeTab === 'library'} onClick={() => setActiveTab('library')} />
          </nav>
        </div>

        <div className="p-4 border-t border-slate-100">
          <button onClick={logout} className="w-full flex items-center justify-center gap-3 px-4 py-3 text-red-500 hover:bg-red-50 rounded-xl transition-colors font-medium border border-transparent hover:border-red-100">
            <LogOut className={`w-5 h-5 ${language === 'fr' ? 'rotate-180' : ''}`} /> {t('admin.sidebar.logout')}
          </button>
        </div>
      </motion.aside>

      <main className="flex-1 p-8 overflow-y-auto relative pt-20">
        <div className="absolute top-0 end-0 w-[500px] h-[500px] bg-indigo-500/5 rounded-full filter blur-3xl pointer-events-none"></div>
        <header className="mb-10 relative z-10">
          <h1 className="text-3xl font-extrabold text-slate-900 mb-2">{t('admin.header.title')}</h1>
          <p className="text-slate-500">{t('admin.header.subtitle')}</p>
        </header>

        <AnimatePresence mode="wait">
          <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}>
            {activeTab === 'overview' && renderOverview()}
            {activeTab === 'accounts' && renderAccountManagement()}
            {activeTab === 'units' && renderUnitsAndWorkshops()}
            {activeTab === 'library' && renderLibraryMonitor()}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* النوافذ المنبثقة */}
      <AnimatePresence>
        {isWorkshopSettingsOpen && editingWorkshop && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
            <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }} className="bg-white border border-slate-200 rounded-3xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col">
              <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2"><Settings className="w-6 h-6 text-blue-600" /> {t('admin.modals.wsSettingsTitle')}</h2>
                <button onClick={() => { setIsWorkshopSettingsOpen(false); setEditingWorkshop(null); }} className="text-slate-400 hover:text-slate-800 p-2 rounded-xl hover:bg-slate-200 transition-colors"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={saveWorkshopSettings} className="p-6 space-y-5 overflow-y-auto max-h-[60vh]">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">{t('admin.modals.wsName')}</label>
                  <input type="text" value={editWsName} onChange={(e) => setEditWsName(e.target.value)} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none" required />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">{t('admin.modals.wsDesc')}</label>
                  <textarea value={editWsDesc} onChange={(e) => setEditWsDesc(e.target.value)} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none h-20" required />
                </div>
                <button type="submit" className="w-full py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold shadow-lg shadow-blue-600/20 transition-all mt-4">{t('admin.modals.saveChangesBtn')}</button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isEditAccountModalOpen && editingAccount && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
            <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }} className="bg-white border border-slate-200 rounded-3xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col">
              <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2"><Edit2 className="w-6 h-6 text-blue-600" /> {t('admin.modals.editLoginTitle')}</h2>
                <button onClick={() => { setIsEditAccountModalOpen(false); setEditingAccount(null); }} className="text-slate-400 hover:text-slate-800 p-2 rounded-xl hover:bg-slate-200 transition-colors"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={handleSaveAccountEdit} className="p-6 space-y-5">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">{t('admin.addEdu.fullName')}</label>
                  <input type="text" value={editingAccount.name} onChange={(e) => setEditingAccount({...editingAccount, name: e.target.value})} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none text-start" required />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">{t('admin.addEdu.workshopLabel')}</label>
                  <select value={editingAccount.workshop} onChange={(e) => setEditingAccount({...editingAccount, workshop: e.target.value})} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none text-start">
                    {workshops.map(ws => <option key={ws.id} value={ws.name}>{ws.name}</option>)}
                  </select>
                </div>
                <button type="submit" className="w-full py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold shadow-lg shadow-blue-600/20 transition-all mt-4">{t('admin.modals.saveChangesBtn')}</button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isAddUnitModalOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
            <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }} className="bg-white border border-slate-200 rounded-3xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col">
              <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2"><FolderKanban className="w-6 h-6 text-emerald-600" /> {t('admin.modals.addUnitTitle')}</h2>
                <button onClick={() => setIsAddUnitModalOpen(false)} className="text-slate-400 hover:text-slate-800 p-2 rounded-xl hover:bg-slate-200 transition-colors"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={handleAddUnit} className="p-6 space-y-5">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">{t('admin.modals.unitName')}</label>
                  <input type="text" value={newUnitData.name} onChange={(e) => setNewUnitData({...newUnitData, name: e.target.value})} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none" required />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">{t('admin.modals.managerName')}</label>
                  <input type="text" value={newUnitData.manager} onChange={(e) => setNewUnitData({...newUnitData, manager: e.target.value})} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none" required />
                </div>
                <button type="submit" className="w-full py-4 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-bold shadow-lg mt-4">{t('admin.modals.addUnitSubmit')}</button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isAddWorkshopModalOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
            <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }} className="bg-white border border-slate-200 rounded-3xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col">
              <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2"><Briefcase className="w-6 h-6 text-slate-800" /> {t('admin.modals.addWorkshopTitle')}</h2>
                <button onClick={() => setIsAddWorkshopModalOpen(false)} className="text-slate-400 hover:text-slate-800 p-2 rounded-xl hover:bg-slate-200 transition-colors"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-4 bg-blue-50 border-b border-blue-100 text-sm font-bold text-blue-800">
                {t('admin.modals.addedUnder')} <span className="text-blue-600">{selectedUnit?.name}</span>
              </div>
              <form onSubmit={handleAddWorkshop} className="p-6 space-y-5">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">{t('admin.modals.wsName')}</label>
                  <input type="text" value={newWorkshopData.name} onChange={(e) => setNewWorkshopData({...newWorkshopData, name: e.target.value})} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-slate-800 focus:outline-none" required />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">{t('admin.modals.desc')}</label>
                  <textarea value={newWorkshopData.description} onChange={(e) => setNewWorkshopData({...newWorkshopData, description: e.target.value})} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-slate-800 focus:outline-none resize-none h-24" required />
                </div>
                <button type="submit" className="w-full py-4 bg-slate-900 text-white rounded-xl font-bold shadow-lg mt-4">{t('admin.modals.approveWorkshopBtn')}</button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {previewActivity && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4 md:p-8">
            <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }} className="bg-white border border-slate-200 rounded-3xl w-full max-w-4xl shadow-2xl overflow-hidden flex flex-col max-h-full">
              <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50 shrink-0">
                <div>
                  <h2 className="text-xl font-extrabold text-slate-800 flex items-center gap-2 mb-1"><MonitorPlay className="w-6 h-6 text-indigo-600" /> {t('admin.modals.previewTitle')}</h2>
                  <p className="text-xs font-bold text-slate-500 flex items-center gap-2"><Users className="w-3.5 h-3.5"/> {t('admin.libraryMonitor.designBy')} {previewActivity.author}</p>
                </div>
                <button onClick={() => setPreviewActivity(null)} className="text-slate-400 hover:text-slate-800 p-2 rounded-xl hover:bg-slate-200 transition-colors bg-white border border-slate-200"><X className="w-5 h-5" /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-6 bg-slate-200/50 relative">
                 <div className="bg-slate-50 border border-slate-200 shadow-inner rounded-3xl max-w-3xl mx-auto p-8 min-h-[400px]">
                    <h1 className="text-2xl font-bold text-slate-800 mb-10 text-center border-b border-slate-200 pb-4">{previewActivity.title}</h1>
                    <div className="space-y-8">
                      {previewActivity.elements && previewActivity.elements.map((el) => (
                        <div key={el.id} className="w-full flex justify-center">
                          {el.type === 'text' && <p style={{ fontSize: `${Math.max(14, el.fontSize * 0.8)}px`, fontWeight: el.fontWeight, textAlign: el.textAlign, color: el.color === '#ffffff' ? '#1e293b' : el.color }} className="w-full leading-relaxed bg-white p-6 rounded-2xl shadow-sm border border-slate-200">{el.content}</p>}
                          {el.type === 'image' && <div className="rounded-2xl overflow-hidden shadow-sm border-4 border-white max-w-full"><img src={el.content} className="max-w-full h-auto object-cover max-h-64" alt="معاينة" /></div>}
                          {el.type === 'video' && <div className="w-full rounded-2xl overflow-hidden shadow-sm border-4 border-white bg-black"><video src={el.content} className="w-full max-h-64 object-cover" controls /></div>}
                        </div>
                      ))}
                    </div>
                 </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* المربع المنبثق لبيانات الحساب المولد حديثاً (موجود داخل الـ return الأساسي) */}
      <AnimatePresence>
        {newAccountData && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
            <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }} className="bg-white rounded-3xl p-8 max-w-sm w-full shadow-2xl border border-slate-200">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-8 h-8" />
                </div>
              </div>
              <h3 className="text-xl font-extrabold text-slate-800 mb-6 text-center">تم توليد الحساب بنجاح!</h3>
              <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200 mb-6 relative overflow-hidden">
                <div className="absolute top-0 end-0 w-16 h-16 bg-indigo-500/5 rounded-full filter blur-xl"></div>
                <p className="text-xs font-bold text-slate-500 mb-1">البريد الإلكتروني للإطار:</p>
                <p className="text-sm font-black text-slate-800 mb-4 select-all">{newAccountData.email}</p>
                <p className="text-xs font-bold text-slate-500 mb-1">كلمة المرور المؤقتة:</p>
                <p className="text-lg font-black text-indigo-600 select-all tracking-wider">{newAccountData.password}</p>
              </div>
              <button 
                onClick={() => {
                  navigator.clipboard.writeText(`تم إنشاء حسابك في منصة تمكين:\nالإيميل: ${newAccountData.email}\nكلمة المرور: ${newAccountData.password}`);
                  alert("تم نسخ البيانات بنجاح، يمكنك لصقها وإرسالها للإطار.");
                  setNewAccountData(null);
                }}
                className="w-full py-3.5 bg-indigo-600 text-white font-bold rounded-xl mb-3 hover:bg-indigo-500 shadow-lg shadow-indigo-600/20 transition-all flex justify-center items-center gap-2"
              >
              <Copy className="w-5 h-5" /> نسخ البيانات للإطار
              </button>
              <button onClick={() => setNewAccountData(null)} className="w-full py-2.5 text-slate-500 font-bold hover:text-slate-800 bg-slate-100 hover:bg-slate-200 rounded-xl transition-all">إغلاق</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ... المكونات المساعدة (SidebarItem و StatCard) تبقى كما هي بالأسفل ...
const SidebarItem = ({ icon, label, active, onClick }) => (
  <button onClick={onClick} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${active ? 'bg-slate-900 text-white shadow-md' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-800'}`}>
    {React.cloneElement(icon, { className: "w-5 h-5" })}
    <span className="font-bold text-sm">{label}</span>
  </button>
);

const StatCard = ({ title, value, trend }) => (
  <motion.div whileHover={{ y: -5 }} className="bg-white border border-slate-200 p-6 rounded-3xl shadow-sm flex flex-col justify-between h-full">
    <h4 className="text-slate-500 font-bold text-sm mb-2">{title}</h4>
    <div className="text-4xl font-extrabold text-slate-900 mb-2">{value}</div>
    <div className="text-xs font-bold text-emerald-500 bg-emerald-50 w-fit px-2 py-1 rounded-md border border-emerald-100">{trend}</div>
  </motion.div>
);

export default AdminDashboard;